/* global Blockly */
import React from 'react';
import PropTypes from 'prop-types';
import util from '../../../../../../../utils/es5Utils';
import {checkBoxListIcon} from "./index";

function defineGenerators(context) {
    let widgets = util.widgetsOfType(context, 'checkboxlist');
    return {
        /**
         * @return {string}
         */
        "JavaScript": function (block) {
            const id = block.getFieldValue("ID");
            const widgetExists = widgets.some(function (x) {
                return x.id === id;
            });
            if (!widgetExists) {
                console.warn('unable to find checkbox with id:' + id);
                return `console.warn('unable to find checkbox with id: ${id}');\n`;
            }

            // get block value (js-compatible)
            const checkboxValue = Blockly.JavaScript.valueToCode(block, "CB", Blockly.JavaScript.ORDER_NONE);
            // by default, get checkbox value as a text (string constant) from the dropdown selector, i.e. "Option 1"
            let cb = `"${util.parseString(checkboxValue)}"`;
            const checkboxValueInput = block.getInput('CB');
            if (checkboxValueInput.connection.isConnected()) {
                let checkboxValueTargetBlock = checkboxValueInput.connection.targetBlock();
                if (!!checkboxValueTargetBlock && !checkboxValueTargetBlock.isShadow_) {
                    // non-default block is connected, which means the result value is not a text string, but rather a js code
                    cb = checkboxValue;
                }
            }

            const value = Blockly.JavaScript.valueToCode(block, "VALUE", Blockly.JavaScript.ORDER_NONE);
            const by = block.getFieldValue("BY");
            return `Snap.widgets.checkbox.setValue("${id}", ${value}, "${by}", ${cb});\n`;
        }
    }
}

function defineBlock(context) {
    let checkboxlists = util.widgetsOfType(context, 'checkboxlist');
    let checkboxOpts = {};
    checkboxlists.forEach(function (widget) {
        checkboxOpts[widget.id] = {
            text: widget.properties.checkboxes.map(function (cb) {
                return [cb.name, cb.name]
            }),
            value: widget.properties.checkboxes.map(function (cb) {
                return [cb.properties.value, cb.properties.value]
            })
        }
    });

    return {
        init: function () {
            let widgetDD = new Blockly.FieldWidgetsDropdown(context, "checkboxlist", undefined, '- select -');
            let textValueDD = new Blockly.FieldDropdown([['text', 'text'], ['value', 'value']]);
            this.appendDummyInput()
                .appendField("in")
                .appendField(new Blockly.FieldImage(checkBoxListIcon, 30, 30, "check box list icon", null), 'checkBoxListIcon')
                .appendField(widgetDD, "ID")
                .appendField("set checkbox with")
                .appendField(textValueDD, "BY");
            this.appendValueInput("CB").setCheck("String");
            this.appendValueInput("VALUE")
                .appendField('to')
                .setCheck("Boolean");
            widgetDD.setOnChange(this.updateByWidget.bind(this));
            textValueDD.setOnChange(this.updateByType.bind(this));

            this.setPreviousStatement(true);
            this.setNextStatement(true);
            this.setColour(Blockly.Msg.WIDGET_VALUES_HUE);
            this.setTooltip('Set a checkbox in the checkbox list to checked or unchecked');
            this.setHelpUrl(Blockly.BASE_HELP_URL + '#checkbox-lists');
            this.update(this.getFieldValue("ID"), this.getFieldValue("BY"));
        },
        onchange: function() {
            if (this.getField('checkBoxListIcon')?.imageElement_) {
                this.getField('checkBoxListIcon').setTooltip('Checkbox List');
            }
        },
        updateByType: function (val) {
            this.update(this.getFieldValue('ID'), val)
        },
        updateByWidget: function (val) {
            this.update(val, this.getFieldValue('BY'))
        },
        onLoad: function(){
            let id= this.getFieldValue('ID');
            let by= this.getFieldValue('BY');
            let widget = checkboxlists.find(function (x) {return x.id === id});
            let opts;
            if (widget) {
                opts = checkboxOpts[id][by] || [['', '']];
            } else {
                console.warn('widget not found: '+id);
                opts = [['','']];
            }

            let value = Blockly.JavaScript.valueToCode(this, 'CB', Blockly.JavaScript.ORDER_NONE);
            
            if (!opts.filter(function(opt) { return opt[1] === value }).length) {
                console.warn('option not found: '+value);
                let input = this.getInput('CB');
                let valuesDDBlock = input.connection.targetBlock();
                if (valuesDDBlock.type === 'dropdown_options') {
                    let dd = valuesDDBlock.setFieldDropdown(opts);
                    dd.setToSelectOption();
                    dd.setWarning(true);
                    let closure = dd.onChange;

                    dd.setOnChange(function() {
                        dd.setWarning(false);
                        if (closure) {
                            closure();
                            dd.setOnChange(closure);
                        }
                    });
                }
            }
        },
        // update the last input
        update: function (val, by) {

            let input = this.getInput('CB');
            let existingBlock = input.connection.targetBlock();
            let block = this.workspace.newBlock('dropdown_options');
            block.setShadow(true);

            if (this.getField('ID').hasBeenSet) {
                let widget = checkboxlists.find(function (x) {return x.id === val});
                let opts;
                if (widget) {
                    opts = checkboxOpts[val][by];
                } else {
                    console.warn('widget ' + val + " not found");
                    opts = [['','']]
                }
                block.setFieldDropdown(opts, undefined, '- select -');
            } else {
                let dd = block.setFieldDropdown([['','']], undefined, '- select -');
                dd.setEnabled(false);
            }
            input.connection.connect(block.outputConnection);

            // If there was already a block connected to this input, re-connect it to the input.
            // Still needed to connect the shadow block prior so if user then removes the added block,
            // the underlying shadow block still shows the correct drop-down options for that widget and text/ value option
            if (existingBlock && !existingBlock.isShadow()) {
                input.connection.connect(existingBlock.outputConnection);
                if (this.rendered) {
                    existingBlock.initSvg();
                    existingBlock.render();
                }
            } else if (this.rendered) {
                block.initSvg();
                block.render();
            }
        }
    }
}


const SetCheckboxlistOption = function (props) {
    let name = "snap_set_checkboxlist_option";
    SetCheckboxlistOption.blockName = name;

    Blockly.Blocks[name] = defineBlock(props.context);
    let generators = defineGenerators(props.context);
    for (let i in generators) {
        Blockly[i][name] = generators[i];
    }

    if (props.widgets.length < 1) {
        return null;
    }

    return (
        <block type={name}>
            {props.widget?.id ? <field name='ID'>{props.widget.id}</field> : null}
            <value name="VALUE">
                <shadow type="checkbox"/>
            </value>
        </block>
    );
}

SetCheckboxlistOption.propTypes = {
    context: PropTypes.object.isRequired,
    widgets: PropTypes.array.isRequired,
    widget: PropTypes.object,
};

export default SetCheckboxlistOption;
